import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  Typography,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import { Google } from '@mui/icons-material';

const Login = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  // Check for OAuth callback URL and redirect to OAuth callback component
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      // If we have an OAuth code, redirect to the OAuth callback component
      navigate('/oauth-callback' + window.location.search);
    }
  }, [navigate]);


  const handleDevLogin = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/dev-login`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) {
        setError('Dev login not available');
        return;
      }
      const data = await response.json();
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        window.dispatchEvent(new Event('authChanged'));
        navigate('/');
      }
    } catch (err) {
      setError('Dev login failed - is the backend running?');
    }
  };

  const handleGoogleLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    if (!clientId) {
      setError('Google Client ID not configured');
      return;
    }

    const redirectUri = `${window.location.origin}`;
    const scope = 'openid email profile';
    const responseType = 'code';

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${encodeURIComponent(clientId)}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=${encodeURIComponent(scope)}&` +
      `response_type=${responseType}&` +
      `access_type=offline&` +
      `hd=nkpmedical.com`;

    window.location.href = authUrl;
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: (theme) => `url(/NKP-Background-1.png), linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) => `${theme.palette.primary.light}30`,
          zIndex: 1
        }
      }}
    >
      <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
        <Card
          elevation={24}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
            bgcolor: 'background.paper'
          }}
        >
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              p: 4,
              textAlign: 'center'
            }}
          >
            <Box sx={{ mb: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
              <img 
                src="/nkp-logo.svg"
                alt="NKP Medical"
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  height: 'auto',
                }}
              />
            </Box>
            <Typography
              component="h1"
              variant="h2"
              sx={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 400,
                mb: 1,
                letterSpacing: '0.05em',
                color: 'white',
                fontSize: { xs: '2.5rem', sm: '3rem' }
              }}
            >
              Nerv
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 400,
                opacity: 0.85,
                color: 'white',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                fontSize: '0.8rem'
              }}
            >
              NKP Internal Platform
            </Typography>
          </Box>
          
          <CardContent sx={{ p: 4 }}>
            <Typography 
              variant="h5" 
              align="center" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                mb: 1
              }}
            >
              Welcome Back
            </Typography>
            <Typography 
              variant="body1" 
              align="center" 
              color="text.secondary" 
              paragraph
              sx={{ mb: 3 }}
            >
              Access your site management dashboard
            </Typography>
            
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2
                }}
              >
                {error}
              </Alert>
            )}
            
            <Button
              onClick={handleGoogleLogin}
              fullWidth
              variant="contained"
              size="large"
              startIcon={<Google />}
              sx={{
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                textTransform: 'none',
                mb: 2,
                bgcolor: 'primary.light',
                '&:hover': {
                  bgcolor: 'primary.main'
                }
              }}
            >
              Sign in with Google (@nkpmedical.com)
            </Button>
            
            
            {process.env.REACT_APP_ENVIRONMENT === 'development' && (
              <Button
                onClick={handleDevLogin}
                fullWidth
                variant="outlined"
                size="large"
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: 'none',
                  mb: 2,
                  borderColor: 'secondary.main',
                  color: 'secondary.main',
                  '&:hover': {
                    borderColor: 'secondary.dark',
                    color: 'secondary.dark',
                    bgcolor: 'action.hover'
                  }
                }}
              >
                Dev Login (skip Google OAuth)
              </Button>
            )}

            <Box
              sx={{
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 2,
                textAlign: 'center'
              }}
            >
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                🔒 Domain Restricted Access
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                Google OAuth is restricted to @nkpmedical.com email addresses only
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;